import { useMemo, useState } from "react";
import clsx from "clsx";
import { Field, Modal } from "../../../../components";
import { useStore } from "../../../../stores";
import { Formik } from "formik";
import locale from "../../../../constants/locale";
import { toast } from "bulma-toast";
import Chat from "../../../teammates/components/Chat";
import { useTranslation } from "react-i18next";
import { EdnaChatType } from "../../../../models";

export default function GuestUser() {
  const { t } = useTranslation();
  const { user,userHasAdminAccess } = useStore("auth");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { users } = useStore("users");
  const { updateGuestUsers, selectedChat } = useStore("chats");
  const { selectedBot } = useStore("bots");

  const filteredAgents = useMemo(
    () =>
      users
        ?.filter((f) => {
          if (f.id === user?.id) return false;
          if (!search) return true;
          return f.name?.toLowerCase()?.includes(search.toLowerCase());
        })
        ?.map((user) => ({
          id: user.id,
          name: user.name || "",
          image: user?.avatarUrl || "/assets/avatar.svg",
          type: EdnaChatType.SINGLE,
          userStatus: user.status,
        })) || [],
    [search, users, user?.id]
  );

  if (!userHasAdminAccess && !selectedBot?.settings?.guestModeEnabled) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <Formik
          initialValues={{
            userIds: ((value?: string) => {
              try {
                return JSON.parse(value ?? "");
              } catch (e) {
                return [];
              }
            })(selectedChat?.data?.guest_users),
          }}
          onSubmit={async (values: any, { resetForm }) => {
            try {
              await updateGuestUsers(values.userIds);

              toast({
                message: t(locale.messageGuestUserUpdateSuccess),
                position: "top-right",
                dismissible: true,
                type: "is-success",
                pauseOnHover: true,
              });
              resetForm();
              setIsOpen(false);
            } catch (e) {
              console.log("error", e);
              toast({
                message: t(locale.genericError),
                position: "top-right",
                dismissible: true,
                type: "is-danger",
                pauseOnHover: true,
              });
            }
          }}
        >
          {({
            isSubmitting,
            submitForm,
            resetForm,
            values,
            setFieldValue,
          }: any) => (
            <Modal
              isOpen={isOpen}
              title={t(locale.addGuestUser)}
              onClose={() => {
                resetForm();
                setIsOpen(false);
              }}
              primaryLoading={isSubmitting}
              primaryText={t(locale.add)}
              onPrimary={submitForm}
              allowCardOverflow
            >
              <div className="field">
                <label className="label">{t(locale.search)}</label>
                <div className="control">
                  <input
                    name="search"
                    className="input"
                    placeholder={t(locale.search)}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <Field name="userIds" type="hidden" />
              {filteredAgents.length === 0 && (
                <div className="has-text-grey">{t(locale.noResult)}</div>
              )}
              {filteredAgents?.map((chat) => {
                return (
                  <SelectableChat
                    chat={chat}
                    isSelected={values.userIds.includes(chat.id)}
                    onChange={() => {
                      if (values.userIds.includes(chat.id)) {
                        setFieldValue(
                          "userIds",
                          values.userIds.filter((f: any) => f !== chat.id)
                        );
                      } else {
                        setFieldValue("userIds", [...values.userIds, chat.id]);
                      }
                    }}
                  />
                );
              })}
            </Modal>
          )}
        </Formik>
      )}

      <button
        className={clsx("button has-text-primary", {
          "is-ghost is-no-box-shadow": true,
        })}
        onClick={() => setIsOpen(true)}
      >
        <span style={{ position: "absolute" }} className="icon">
          <img src="/assets/add-user.svg" alt="" />
        </span>
      </button>
    </>
  );
}

const SelectableChat = ({ chat, isSelected, onChange }: any) => {
  return (
    <div className="mx-3 py-1 has-rounded-corners">
      <div className="control">
        <label className="checkbox w-100 is-flex is-flex is-align-items-center">
          <input
            type="checkbox"
            className="mr-2"
            onChange={onChange}
            checked={isSelected}
          />
          <Chat chat={chat} disableOnClick />
        </label>
      </div>
    </div>
  );
};
